/**
 * Port of `src/app/(auth)/login/page.tsx`.
 *
 * Copied verbatim, hero panel and mock dashboard included, so the sign-in
 * screen is pixel identical.
 *
 * ## V5-09/V5-10 — Google sign-in is live
 *
 * The Server Component used to read the Google env vars at request time; a
 * static SPA cannot. It now reads `GET /py-api/v1/auth/google/config` instead,
 * which is strictly better than the build-time alternative: the family admin
 * turns Google on, or falls back to passwords, by editing the server
 * environment — no SPA rebuild, no redeploy, and the rollback is immediate.
 *
 * Both methods render while the flag allows it. That overlap is deliberate and
 * temporary: cutting straight over means one Google misconfiguration locks all
 * eight family members out of their own finances with no way back in. The
 * password form disappears the day `AIM_PASSWORD_LOGIN_ENABLED=false` is set
 * on the server.
 *
 * The Google button is rendered HERE rather than inside `LoginForm`, whose
 * `googleEnabled` branch predates this work and still draws a disabled
 * placeholder. It is passed `false` so only one Google button exists.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LoginForm } from "@/components/auth/LoginForm";
import { GoogleSignInButton } from "@/lib/auth/GoogleSignInButton";
import { CONFIG_FALLBACK, fetchGoogleAuthConfig, type GoogleAuthConfig } from "@/lib/auth/google";
import { useAuth } from "@/lib/auth/AuthProvider";
import { AuthError } from "@/lib/auth/types";
import { CodeInput } from "@/pages/security/CodeInput";
import { Wallet, ShieldCheck, ArrowLeft, KeyRound } from "lucide-react";

export function LoginPage() {
  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  // Wave 3 (W12) — set the instant `LoginForm` reports `mfa_required`. Its
  // presence is what swaps the password form for the code-entry step; it is
  // cleared on "back to login" or a completed sign-in.
  const [mfaToken, setMfaToken] = useState<string | null>(null);

  // Starts as the fallback (Google off, passwords on) so the very first paint
  // already offers a usable way in, rather than an empty panel that fills in.
  const [config, setConfig] = useState<GoogleAuthConfig>(CONFIG_FALLBACK);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchGoogleAuthConfig().then((next) => {
      if (!cancelled) setConfig(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCredential = useCallback(
    async (credential: string) => {
      setGoogleError(null);
      setBusy(true);
      try {
        await loginWithGoogle(credential);
        navigate("/dashboard", { replace: true });
      } catch (err) {
        // These two are not errors to apologise for — they are outcomes with
        // a screen of their own, already built for exactly this flow.
        if (err instanceof AuthError && err.code === "access_request_pending") {
          navigate(
            `/access-request-pending?email=${encodeURIComponent(err.email ?? "")}`,
            { replace: true },
          );
          return;
        }
        if (err instanceof AuthError && err.code === "account_disabled") {
          navigate(
            `/access-request-pending?status=disabled&email=${encodeURIComponent(err.email ?? "")}`,
            { replace: true },
          );
          return;
        }
        setGoogleError(
          err instanceof AuthError
            ? err.message
            : "Google sign-in failed. Please try again.",
        );
        setBusy(false);
      }
    },
    [loginWithGoogle, navigate],
  );

  // The button only appears once the server says Google is configured AND
  // named a client id — without one there is no `aud` to pin server-side, and
  // the exchange would refuse every token anyway.
  const showGoogle = config.enabled && Boolean(config.clientId);
  const showPasswords = config.passwordLoginEnabled;

  return (
    <div className="flex min-h-screen bg-[var(--cp-canvas)]">
      {/* Left hero panel — hidden on mobile. SDR navy chrome. */}
      <div className="hidden flex-1 lg:flex">
        <div className="relative flex w-full flex-col justify-between overflow-hidden bg-[var(--navy-900)] p-12 text-white">
          {/* Decorative blobs */}
          <div className="pointer-events-none absolute -right-20 -top-20 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-[var(--cp-accent)]/20 blur-3xl" />

          {/* Top-left brand */}
          <div className="relative flex items-center gap-2">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-lg"
              style={{ background: "linear-gradient(135deg, var(--cp-accent), var(--cp-accent-2))" }}
            >
              <Wallet className="h-5 w-5" />
            </div>
            <span className="text-base font-semibold tracking-tight">AIM</span>
          </div>

          {/* Center content */}
          <div className="relative flex flex-1 flex-col items-center justify-center px-4 text-center">
            <h1 className="text-4xl font-bold leading-tight xl:text-5xl">
              Your Family&apos;s Finances
              <br />
              in One Place
            </h1>

            <div className="mt-12 w-full max-w-md">
              <DashboardMockup />
            </div>

            <p className="mt-10 max-w-sm text-base text-white/85">
              Track income, expenses, loans, taxes and rent — all in one private dashboard for the people you trust.
            </p>
          </div>

          {/* Bottom wordmark */}
          <div className="relative flex items-center justify-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white text-[var(--cp-accent)]">
              <Wallet className="h-4 w-4" />
            </div>
            <span className="text-2xl font-bold tracking-tight">AIM</span>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex flex-1 flex-col items-center justify-center px-4 py-12 sm:px-8">
        <div className="w-full max-w-sm">
          {/* Mobile-only brand */}
          <div className="mb-10 flex items-center justify-center gap-2 lg:hidden">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-lg text-white"
              style={{ background: "linear-gradient(135deg, var(--cp-accent), var(--cp-accent-2))" }}
            >
              <Wallet className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xl font-bold text-[var(--cp-ink)]">AIM</div>
              <div className="text-[11px] text-[var(--cp-ink-3)]">
                Accounting &amp; Investment Manager
              </div>
            </div>
          </div>

          <h2 className="mb-8 text-center text-3xl font-semibold text-[var(--cp-ink)]">
            {mfaToken ? "Verify it's you" : "Log In"}
          </h2>

          {mfaToken ? (
            <MfaChallengeStep mfaToken={mfaToken} onBack={() => setMfaToken(null)} />
          ) : (
            <>
              {googleError && (
                <div className="mb-5 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
                  {googleError}
                </div>
              )}

              {showGoogle && (
                <div className="mb-5">
                  <GoogleSignInButton
                    clientId={config.clientId as string}
                    onCredential={handleCredential}
                    // GIS failing to load is not a dead end while passwords are
                    // still on — say so, and leave the form below usable.
                    onUnavailable={setGoogleError}
                    disabled={busy}
                  />
                  <p className="mt-3 text-center text-xs text-[var(--cp-ink-3)]">
                    AIM never sees your Google password.
                  </p>
                </div>
              )}

              {showGoogle && showPasswords && (
                <div className="relative mb-5">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-[var(--cp-line)]" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-[var(--cp-canvas)] px-3 text-xs font-medium uppercase tracking-wider text-[var(--cp-ink-3)]">
                      or
                    </span>
                  </div>
                </div>
              )}

              {/* `googleEnabled={false}` on purpose — the live button is above. */}
              {showPasswords && (
                <LoginForm googleEnabled={false} onMfaRequired={setMfaToken} />
              )}

              {!showPasswords && !showGoogle && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
                  No sign-in method is enabled on this server. Ask your family admin
                  to check the AIM configuration.
                </div>
              )}

              <p className="mt-10 text-center text-xs text-[var(--cp-ink-3)]">
                Need an account? Ask your family admin to create one.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Wave 3 (W12) — the code-entry step `LoginPage` swaps in when `LoginForm`
 * reports `mfa_required`. Two input modes share one submit path: the 6-digit
 * auto-advancing `CodeInput` for a TOTP code, or a single text field for one
 * of the 10 recovery codes — `completeMfaLogin` accepts either at the same
 * endpoint, so this component does not need to know which kind it sent.
 */
function MfaChallengeStep({ mfaToken, onBack }: { mfaToken: string; onBack: () => void }) {
  const { completeMfaLogin } = useAuth();
  const navigate = useNavigate();

  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [code, setCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(
    async (candidate: string) => {
      if (!candidate || busy) return;
      setBusy(true);
      setError(null);
      try {
        await completeMfaLogin(mfaToken, candidate);
        navigate("/dashboard", { replace: true });
      } catch (err) {
        // Server messages shown verbatim — this file does not paraphrase an
        // "invalid code" / "code expired" / "too many attempts" distinction
        // it was not told about.
        setError(err instanceof AuthError ? err.message : "Something went wrong. Please try again.");
        setBusy(false);
      }
    },
    [busy, completeMfaLogin, mfaToken, navigate],
  );

  function handleRecoverySubmit(event: React.FormEvent) {
    event.preventDefault();
    void submit(recoveryCode.trim());
  }

  return (
    <div className="space-y-5">
      <p className="text-center text-sm text-[var(--cp-ink-2)]">
        {useRecoveryCode
          ? "Enter one of your 10 recovery codes. Each one works only once."
          : "Enter the 6-digit code from your authenticator app."}
      </p>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-center text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </div>
      )}

      {useRecoveryCode ? (
        <form onSubmit={handleRecoverySubmit} className="space-y-4">
          <input
            type="text"
            autoComplete="one-time-code"
            autoFocus
            disabled={busy}
            value={recoveryCode}
            onChange={(e) => setRecoveryCode(e.target.value)}
            placeholder="xxxxx-xxxxx"
            aria-label="Recovery code"
            className="block w-full rounded-xl border border-[var(--cp-line)] bg-[var(--cp-surface)] px-4 py-3 text-center text-sm tracking-wider shadow-sm transition placeholder:text-[var(--cp-ink-3)] focus:border-[var(--cp-accent)] focus:outline-none focus:ring-[3px] focus:ring-[var(--cp-accent-wash)] text-[var(--cp-ink)]"
          />
          <button
            type="submit"
            disabled={busy || !recoveryCode.trim()}
            className="w-full rounded-full bg-[var(--cp-accent)] px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[var(--cp-accent)] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? "Verifying…" : "Verify"}
          </button>
        </form>
      ) : (
        <div className="space-y-4">
          <CodeInput
            length={6}
            value={code}
            onChange={setCode}
            onComplete={(value) => void submit(value)}
            disabled={busy}
            autoFocus
            error={Boolean(error)}
            label="Verification code digit"
          />
          {busy && (
            <p className="text-center text-xs text-[var(--cp-ink-3)]">Verifying…</p>
          )}
        </div>
      )}

      <div className="flex items-center justify-between text-xs">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 font-medium text-[var(--cp-ink-3)] hover:text-[var(--cp-ink)]"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to login
        </button>
        <button
          type="button"
          onClick={() => {
            setUseRecoveryCode((v) => !v);
            setError(null);
            setCode("");
            setRecoveryCode("");
          }}
          className="flex items-center gap-1 font-medium text-[var(--cp-accent-ink)] hover:underline"
        >
          {useRecoveryCode ? (
            <>
              <ShieldCheck className="h-3.5 w-3.5" />
              Use my authenticator app instead
            </>
          ) : (
            <>
              <KeyRound className="h-3.5 w-3.5" />
              Use a recovery code instead
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function DashboardMockup() {
  return (
    <div className="rounded-2xl bg-white p-5 text-slate-800 shadow-2xl ring-1 ring-black/5">
      <div className="mb-4 flex items-center gap-1.5">
        <div className="h-2.5 w-2.5 rounded-full bg-red-400" />
        <div className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
        <div className="h-2.5 w-2.5 rounded-full bg-green-400" />
      </div>

      <div className="mb-4 grid grid-cols-3 gap-2">
        <KpiTile label="Income" value="₹1.8L" tone="emerald" />
        <KpiTile label="Expenses" value="₹92k" tone="rose" />
        <KpiTile label="Saved" value="₹88k" tone="amber" />
      </div>

      <div className="rounded-lg bg-slate-50 p-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[10px] font-medium uppercase tracking-wider text-slate-400">
            Monthly trend
          </span>
          <span className="text-[10px] font-medium text-emerald-600">+12.4%</span>
        </div>
        <div className="flex h-20 items-end gap-1.5">
          {[42, 58, 38, 64, 72, 55, 80, 68, 88, 75, 92, 82].map((h, i) => (
            <div
              key={i}
              className="flex-1 rounded-sm bg-[#2563eb]"
              style={{ height: `${h}%` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function KpiTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "emerald" | "rose" | "amber";
}) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700",
    rose: "bg-rose-50 text-rose-700",
    amber: "bg-amber-50 text-amber-700",
  } as const;
  return (
    <div className={`rounded-lg p-2.5 ${tones[tone]}`}>
      <div className="text-[10px] font-medium uppercase tracking-wide opacity-80">{label}</div>
      <div className="mt-0.5 text-sm font-bold">{value}</div>
    </div>
  );
}
